import { Box, Card, CardContent, Chip, Stack, Typography } from '@mui/material'
import { FunctionComponent, ReactNode, useState } from 'react'
import { IconName } from '../../../../shared/interfaces/icon-picker.model'
import { AssessmentOutlined, LockOutlined } from '@mui/icons-material'
import { TooltipHover } from '../../miscellaneous-components/tooltip-hover'
import { partnerManagedTooltip } from '../../../../shared/utils/partner-managed-utils'

/**
 * Compact card for the saved analytics reports and custom dashboards lists: a tinted icon
 * square beside the name, details as chips underneath. Replaced the old tall card whose
 * body was a single oversized icon over empty space.
 */
export const AnalyticReportCard: FunctionComponent<{
  icon?: IconName,
  // Optional custom glyph rendered in the icon square instead of the default.
  iconNode?: ReactNode,
  name: string,
  onClick: () => void,
  // Set when this report was pushed from a partner's Library.
  isPartnerManaged?: boolean,
  canClientEdit?: boolean,
  editedByClient?: boolean,
  partnerName?: string | null,
  /** Shows a "Custom dates" chip: the report uses its own date range, not the period's. */
  usesCustomDates?: boolean,
  /** The dimensions this report filters by (e.g. Group, Location); one chip each. */
  filterLabels?: string[],
}> = ({ iconNode, name, onClick, isPartnerManaged, canClientEdit, editedByClient, partnerName, usesCustomDates, filterLabels }) => {
  const [hover, setHover] = useState(false)

  const hasChips = (filterLabels && filterLabels.length > 0) || usesCustomDates || isPartnerManaged || editedByClient

  return (
    <Card sx={{
      // 1px like the outlined Papers on the product pages; the old 2px read heavy at this size.
      height: '100%', boxShadow: 'none', borderWidth: '1px', borderStyle: 'solid', borderColor: hover ? 'primary.main' : 'lightGrey.main', borderRadius: '10px', cursor: 'pointer'
    }}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}>
      <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
        <Stack direction='row' alignItems='center' sx={{ gap: 1.5, minWidth: 0 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: '10px', backgroundColor: 'secondary.main', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'primary.dark',
            '& svg': { fontSize: '1.4rem' },
          }}>
            {iconNode ?? <AssessmentOutlined />}
          </Box>
          <Typography variant='h6' sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>{name}</Typography>
        </Stack>
        {hasChips && (
          <Stack direction='row' sx={{ gap: 0.75, flexWrap: 'wrap', mt: 1.5 }}>
            {(filterLabels ?? []).map((label) => (
              <Chip key={label} size='small' variant='outlined' label={`By ${label}`} sx={{ color: 'text.secondary' }} />
            ))}
            {usesCustomDates && (
              <Chip size='small' variant='outlined' label='Custom dates' sx={{ color: 'text.secondary' }} />
            )}
            {isPartnerManaged && (
              <TooltipHover text={partnerManagedTooltip({ partnerName, canClientEdit })} backgroundColor='white' placement='top'>
                <Chip
                  size='small'
                  variant='outlined'
                  icon={<LockOutlined sx={{ fontSize: '0.9rem' }} />}
                  label={partnerName ? `Managed by ${partnerName}` : 'Managed by your partner'}
                  sx={{ color: 'text.secondary', cursor: 'help' }}
                />
              </TooltipHover>
            )}
            {isPartnerManaged && editedByClient && (
              <Chip size='small' variant='outlined' color='info' label='Edited here' />
            )}
          </Stack>
        )}
      </CardContent>
    </Card >
  )
}
